"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Wrench, Cpu, Gauge, Activity, Layers, Flame, Archive, ArrowUpRight, ShieldCheck, Hammer, Truck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const categories = [{
  id: "cat-01",
  name: "Hand Tools",
  code: "CAT-01",
  description: "Drop-forged wrenches, precision sockets, and impact drivers.",
  icon: Wrench,
  itemsCount: "4 Tiers"
}, {
  id: "cat-02",
  name: "Power Machinery",
  code: "CAT-02",
  description: "Bench drills, metal band saws, and commercial lathes.",
  icon: Cpu,
  itemsCount: "Heavy Duty"
}, {
  id: "cat-03",
  name: "Air & Pneumatics",
  code: "CAT-03",
  description: "Cast-iron reciprocating compressors and regulated air lines.",
  icon: Gauge,
  itemsCount: "175 PSI Max"
}, {
  id: "cat-04",
  name: "Diagnostics & Meters",
  code: "CAT-04",
  description: "true-RMS multimeters, borescope cameras, and pressure testers.",
  icon: Activity,
  itemsCount: "Calibrated"
}, {
  id: "cat-05",
  name: "Metal Fabrication",
  code: "CAT-05",
  description: "Sheet metal brakes, slip rolls, and notchers for clean joints.",
  icon: Layers,
  itemsCount: "16-GA Cap"
}, {
  id: "cat-06",
  name: "Welding Gear",
  code: "CAT-06",
  description: "MIG, TIG, and stick inverters paired with autodarkening hoods.",
  icon: Flame,
  itemsCount: "Dual Voltage"
}, {
  id: "cat-07",
  name: "Tool Storage & Carts",
  code: "CAT-07",
  description: "Heavy-gauge steel roller cabinets and bearing-slide drawers.",
  icon: Archive,
  itemsCount: "2,000 LB Cap"
}, {
  id: "cat-08",
  name: "Hydraulic Presses",
  code: "CAT-08",
  description: "H-frame shop presses, ram attachments, and arbor fixtures.",
  icon: Hammer,
  itemsCount: "50-Ton Rated"
}, {
  id: "cat-09",
  name: "Lifts & Jacks",
  code: "CAT-09",
  description: "Low-profile service jacks, jack stands, and transmission cranes.",
  icon: Truck,
  itemsCount: "ASME Certified"
}, {
  id: "cat-10",
  name: "Steel Workbenches",
  code: "CAT-10",
  description: "Reinforced plate tops, integrated vises, and leveling feet.",
  icon: Layers,
  itemsCount: "Milled Tops"
}, {
  id: "cat-11",
  name: "Safety & Maintenance",
  code: "CAT-11",
  description: "Spill containment, air filtration units, and industrial PPE.",
  icon: ShieldCheck,
  itemsCount: "OSHA Compliant"
}];
export default function Offer() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 14
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0 : 0.24,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="what-we-offer" data-nav="dark" className="bg-background text-foreground border-b border-border py-16 lg:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-12 border-b border-border pb-8 lg:mb-16">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div className="border-l-[3px] border-primary pl-4">
              <span className="font-mono text-xs font-bold uppercase tracking-widest text-primary">
                Catalog Architecture // 11 Categories
              </span>
              <h2 className="mt-1 font-serif text-3xl font-bold uppercase tracking-tight text-foreground sm:text-4xl lg:text-5xl">
                What we offer
              </h2>
            </div>
            <p className="max-w-xl font-sans text-sm text-muted-foreground sm:text-base">
              From hand-operated precision instruments to heavy cast-iron machinery, browse 11 focused hardware categories ready to ship nationwide.
            </p>
          </div>
        </div>

        {/* Asymmetric Split Layout */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-8">
          {/* Left Column: Industrial Photography Feature */}
          <div className="lg:col-span-4">
            <div className="sticky top-24 flex flex-col gap-6 rounded-sm border border-border bg-card p-6 shadow-sm">
              <div className="relative overflow-hidden rounded-sm border border-border bg-muted aspect-[4/5]">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1787497/img/industrial_workshop_machinery.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1787497/img/industrial_workshop_machinery.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1787497/img/industrial_workshop_machinery.png-wvc-sizes" alt="Industrial precision workshop tooling and cast-iron machinery" loading="lazy" decoding="async" className="h-full w-full object-cover grayscale contrast-125" />
                <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                  <Badge variant="outline" className="border-border bg-background/80 font-mono text-[11px] text-muted-foreground backdrop-blur-xs">
                    HEAVY GAUGE SPEC
                  </Badge>
                  <span className="font-mono text-xs font-bold text-primary">
                    33 PRODUCTS
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="inline-block h-2 w-2 rounded-full bg-primary" />
                  <h3 className="font-serif text-lg font-bold uppercase text-foreground">
                    Equipment Tier Standards
                  </h3>
                </div>
                <p className="font-sans text-sm leading-relaxed text-muted-foreground">
                  Every product is sourced for commercial longevity, rigid tolerances, and repeatable accuracy in active service environments.
                </p>
              </div>

              <div className="pt-2">
                <Button asChild className="w-full rounded-sm bg-primary font-serif font-bold uppercase text-primary-foreground hover:bg-primary/90">
                  <Link to="/ShopPage" className="inline-flex items-center justify-center gap-2">
                    Browse full inventory
                    <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          {/* Right Column: 11 Categories Modular Grid */}
          <div className="lg:col-span-8">
            <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
            once: true,
            amount: 0.15
          }} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {categories.map((cat, i) => {
              const IconComponent = cat.icon;
              return <motion.div key={cat.id} variants={itemVariants} data-index={i} className="group relative flex flex-col justify-between rounded-sm border border-border bg-card p-5 transition-[border-color,box-shadow] duration-200 hover:border-primary/60 hover:shadow-md">
                    {/* Top spec & code strip */}
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-sm border border-border bg-secondary text-primary transition-colors group-hover:border-primary/40 group-hover:bg-primary/10">
                        <IconComponent className="h-5 w-5 stroke-[2]" />
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className="font-mono text-[10px] font-bold text-muted-foreground">
                          {cat.code}
                        </span>
                        <Badge variant="secondary" className="rounded-xs border border-border bg-secondary/80 font-mono text-[10px] text-muted-foreground">
                          {cat.itemsCount}
                        </Badge>
                      </div>
                    </div>

                    {/* Middle title and technical description */}
                    <div className="my-4 space-y-1.5">
                      <h4 className="font-serif text-base font-bold uppercase tracking-tight text-foreground transition-colors group-hover:text-primary">
                        {cat.name}
                      </h4>
                      <p className="font-sans text-xs leading-relaxed text-muted-foreground">
                        {cat.description}
                      </p>
                    </div>

                    {/* Bottom active trigger line */}
                    <div className="mt-2 flex items-center justify-between border-t border-border pt-3">
                      <Link to="/ShopPage" className="inline-flex items-center gap-1 font-mono text-xs font-semibold text-muted-foreground transition-colors group-hover:text-primary">
                        <span>View specifications</span>
                        <ArrowUpRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </Link>
                      <div className="h-0.5 w-6 bg-border transition-colors group-hover:bg-primary" />
                    </div>

                    {/* Subtle bottom indicator on card hover */}
                    <div className="absolute inset-x-0 bottom-0 h-[2px] bg-transparent transition-colors group-hover:bg-primary" />
                  </motion.div>;
            })}
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}